import React, { useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@lyearn/i18n';
import SessionItem from '@/modules/corporate/components/ContentsDialog/SessionItemViewer/SessionItem';
import CollectionItem from '@/modules/corporate/modules/group/views/GroupManager/components/CollectionsDialog/CollectionItem';
import ContentItem from '@/modules/corporate/modules/path/views/PathEditor/components/ContentItem';
import {
  AssignmentItemTypeEnum,
  DueDateAssignmentConfigInput,
  DueDateNestingLevelEnum,
} from '@/types/schema';

import { getSelectedDueDate } from '../../../utils';
import { isDueDateError, isTagEmpty } from '../../Editor/config';
import SectionDetails from '../../NestedPathModal/SectionDetails';
import { SetDueDateDialogProps } from '../types';

function useSetDueDate(props: SetDueDateDialogProps) {
  const { t } = useTranslation();
  const {
    inViewItemForDueDate,
    onCloseDueDateDialog,
    assignmentItemsDueDateConfig,
    setAssignmentItemsDueDate,
    onResetDueDate,
    nestedDialogState,
    setNestedDialogState,
  } = props;

  const selectedDueDateData = getSelectedDueDate(
    inViewItemForDueDate,
    assignmentItemsDueDateConfig,
  );
  const [selectedDueDate, setSelectedDueDate] = useState<
    DueDateAssignmentConfigInput | undefined | null
  >(selectedDueDateData?.dueDateConfig);

  const [dueDateError, setDueDateError] = useState<string>();
  const validateDueDateAndGetError = useCallback(() => {
    if (selectedDueDate) {
      if (isDueDateError(selectedDueDate)) {
        return t('AssignmentCreator:DueDateError');
      }
      if (isTagEmpty(selectedDueDate)) {
        return t('AssignmentCreator:TagError');
      }
    }
  }, [selectedDueDate, t]);

  const _setSelectedDueDate = useCallback(
    (val: DueDateAssignmentConfigInput | undefined | null) => {
      setSelectedDueDate(val);
      setDueDateError(undefined);
    },
    [],
  );

  const {
    itemType,
    dueDateNestingLevel,
    editingContent,
    editingCollection,
    editingSection,
    editingSession,
    history,
  } = inViewItemForDueDate;

  const onSetDueDate = useCallback(() => {
    const dueDateError = validateDueDateAndGetError();
    setDueDateError(dueDateError);

    if (dueDateError) {
      return;
    }

    if (selectedDueDate && selectedDueDateData?.itemId) {
      if (inViewItemForDueDate.history) {
        setNestedDialogState((prevState) => {
          return {
            ...prevState,
            [selectedDueDateData.itemId]: {
              itemId: selectedDueDateData?.itemId,
              itemType: inViewItemForDueDate.itemType,
              dueDateConfig: selectedDueDate,
            },
          };
        });
        onCloseDueDateDialog();
      } else {
        setAssignmentItemsDueDate({
          ...assignmentItemsDueDateConfig,
          [selectedDueDateData.itemId]: {
            itemId: selectedDueDateData.itemId,
            itemType: inViewItemForDueDate.itemType,
            dueDateConfig: selectedDueDate,
            dueDateNestingLevel: inViewItemForDueDate.dueDateNestingLevel,
            nestedDueDateConfig: inViewItemForDueDate.nestedDueDateConfig,
          },
        });
        onCloseDueDateDialog();
      }
    }
  }, [
    assignmentItemsDueDateConfig,
    inViewItemForDueDate.dueDateNestingLevel,
    inViewItemForDueDate.history,
    inViewItemForDueDate.itemType,
    inViewItemForDueDate.nestedDueDateConfig,
    onCloseDueDateDialog,
    selectedDueDate,
    selectedDueDateData?.itemId,
    setAssignmentItemsDueDate,
    setNestedDialogState,
    validateDueDateAndGetError,
  ]);

  const onCloseNestedModal = useCallback(() => {
    setNestedDialogState(undefined);
    onCloseDueDateDialog();
  }, [onCloseDueDateDialog, setNestedDialogState]);

  const onReset = useCallback(() => {
    if (selectedDueDateData?.itemId) {
      onResetDueDate(selectedDueDateData.itemId);
      onCloseNestedModal();
    }
  }, [onCloseNestedModal, onResetDueDate, selectedDueDateData?.itemId]);

  const onSetNestedDueDate = useCallback(() => {
    if (selectedDueDateData?.itemId) {
      setAssignmentItemsDueDate({
        ...assignmentItemsDueDateConfig,
        [selectedDueDateData.itemId]: {
          itemId: selectedDueDateData.itemId,
          itemType: inViewItemForDueDate.itemType,
          dueDateNestingLevel: inViewItemForDueDate.dueDateNestingLevel,
          nestedDueDateConfig: nestedDialogState,
        },
      });
      onCloseNestedModal();
    }
  }, [
    assignmentItemsDueDateConfig,
    inViewItemForDueDate.dueDateNestingLevel,
    inViewItemForDueDate.itemType,
    nestedDialogState,
    onCloseNestedModal,
    selectedDueDateData?.itemId,
    setAssignmentItemsDueDate,
  ]);

  const dialogState = useMemo(() => {
    if (!dueDateNestingLevel) {
      if (editingContent) {
        return {
          title: t('AssignmentCreator:SetDueDateSm'),
          description: t('AssignmentCreator:SetDueDateContent'),
          actionPrimaryText: history ? t('Common:Actions.Done') : t('Common:Actions.Save'),
          Content: <ContentItem content={editingContent} />,
          itemId: editingContent.id,
        };
      }

      if (editingCollection) {
        return {
          title: t('AssignmentCreator:SetDueDateSm'),
          description: t('AssignmentCreator:SetDueDateCollection'),
          actionPrimaryText: t('Common:Actions.Save'),
          Content: <CollectionItem group={editingCollection} />,
          itemId: editingCollection.id,
        };
      }

      if (editingSection) {
        return {
          title: t('AssignmentCreator:SetDueDateSm'),
          description: t('AssignmentCreator:SetDueDateSection'),
          actionPrimaryText: history ? t('Common:Actions.Done') : t('Common:Actions.Save'),
          Content: <SectionDetails section={editingSection} />,
          itemId: editingSection.id,
        };
      }

      if (editingSession) {
        return {
          title: t('AssignmentCreator:SetDueDateSm'),
          description: t('AssignmentCreator:SetDueDateSession'),
          actionPrimaryText: t('Common:Actions.Save'),
          Content: <SessionItem event={editingSession} />,
          itemId: editingSession.id,
        };
      }
    }

    if (dueDateNestingLevel === DueDateNestingLevelEnum.Contents) {
      if (itemType === AssignmentItemTypeEnum.Content) {
        return {
          title: t('AssignmentCreator:SetDueDateSm'),
          description: t('AssignmentCreator:SetDueDateContentInPath'),
          actionPrimaryText: t('Common:Actions.Save'),
        };
      }

      if (itemType === AssignmentItemTypeEnum.Collection) {
        return {
          title: t('AssignmentCreator:SetDueDateSm'),
          description: t('AssignmentCreator:SetDueDateContentInCollection'),
          actionPrimaryText: t('Common:Actions.Save'),
        };
      }
    }

    if (dueDateNestingLevel === DueDateNestingLevelEnum.Sections) {
      return {
        title: t('AssignmentCreator:SetDueDateSm'),
        description: t('AssignmentCreator:SetDueDateSectionInPath'),
        actionPrimaryText: t('Common:Actions.Save'),
      };
    }
  }, [
    dueDateNestingLevel,
    editingCollection,
    editingContent,
    editingSection,
    editingSession,
    history,
    itemType,
    t,
  ]);

  return {
    state: {
      dialogState,
      selectedDueDate,
      dueDateError,
    },
    method: {
      setSelectedDueDate: _setSelectedDueDate,
      onSetDueDate,
      onReset,
      onSetNestedDueDate,
      onCloseNestedModal,
    },
  };
}

export default useSetDueDate;
